import { useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  useTheme,
} from '@mui/material';
import { useDrawRequestQueryApi } from '@hooks/api/useDrawRequestQueryApi';
import { DrawRequestDetailResponseModel } from '@models/drawRequests/drawRequestDetailResponseModel';
import { BudgetItemResponse } from '@models/drawRequests/budgetItemResponse';
import FormattedCurrency from '@helpers/formattedCurrency';
import BudgetReadOnlyCards from './BudgetReadOnlyCards';
import { useApp } from '@context/AppContext';

interface BudgetGridReadOnlyViewProps {
  drawDetails: DrawRequestDetailResponseModel[];
  loanRecId: string;
  title?: string;
}

export default function BudgetGridReadOnlyView({
  drawDetails,
  loanRecId,
  title = 'Budget Details',
}: BudgetGridReadOnlyViewProps) {
  const theme = useTheme();
  const { isSmallDesktop } = useApp();

  const { data: budgetItemsData } =
    useDrawRequestQueryApi.useFetchBudgetItems(loanRecId);

  const budgetItemMap = useMemo(() => {
    if (!budgetItemsData) return new Map();

    const map = new Map();
    budgetItemsData.forEach((item: BudgetItemResponse) => {
      map.set(item.recId, `${item.account} - ${item.description}`);
    });
    return map;
  }, [budgetItemsData]);

  const renderTableHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          }}
        >
          Budget Item
        </TableCell>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          }}
          align="right"
        >
          Remaining Budget
        </TableCell>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          }}
          align="right"
        >
          Request
        </TableCell>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          }}
          align="right"
        >
          % Complete
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderBudgetItemCell = (detail: any, index: number) => (
    <TableCell
      sx={{
        color: 'text.primary',
        fontWeight: 500,
      }}
      aria-label={`Budget item ${index + 1}: ${budgetItemMap.get(detail.budgetItemRecId) || detail.budgetItem || 'Budget Item'}`}
    >
      {budgetItemMap.get(detail.budgetItemRecId) ||
        detail.budgetItem ||
        'Budget Item'}
    </TableCell>
  );

  const renderRemainingBudgetCell = (detail: any, index: number) => (
    <TableCell
      sx={{
        color: theme.palette.text.primary,
      }}
      align="right"
      aria-label={`Remaining budget for item ${index + 1}: ${detail.remainingBudget}`}
    >
      <FormattedCurrency value={detail.remainingBudget} />
    </TableCell>
  );

  const renderRequestAmountCell = (detail: any, index: number) => (
    <TableCell
      sx={{
        color: theme.palette.text.primary,
      }}
      align="right"
      aria-label={`Request amount for item ${index + 1}: ${detail.drawAmount}`}
    >
      <FormattedCurrency value={detail.drawAmount} />
    </TableCell>
  );

  const renderPercentCompleteCell = (detail: any, index: number) => (
    <TableCell
      sx={{
        color: theme.palette.text.primary,
      }}
      align="right"
      aria-label={`Percent complete for item ${index + 1}: ${detail.pctCompleted}%`}
    >
      {detail.pctCompleted}%
    </TableCell>
  );

  const renderTableBody = () => (
    <TableBody>
      {drawDetails.map((detail, index) => (
        <TableRow
          key={detail.recId || index}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          aria-label={`Budget item ${index + 1} row`}
        >
          {renderBudgetItemCell(detail, index)}
          {renderRemainingBudgetCell(detail, index)}
          {renderRequestAmountCell(detail, index)}
          {renderPercentCompleteCell(detail, index)}
        </TableRow>
      ))}
    </TableBody>
  );

  const renderSmallScreenView = () => (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
      component="section"
      aria-label="Budget details section"
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          mb: 3,
        }}
      >
        {title}
      </Typography>

      <BudgetReadOnlyCards
        drawDetails={drawDetails}
        budgetItemMap={budgetItemMap}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 2,
          pt: 2,
        }}
        component="div"
        role="text"
        aria-label={`Total draw amount: ${drawTotal}`}
      >
        <Typography variant="h6">
          Draw Total: <FormattedCurrency value={drawTotal} />
        </Typography>
      </Box>
    </Paper>
  );

  const drawTotal = drawDetails.reduce(
    (sum, detail) => sum + detail.drawAmount,
    0
  );

  if (isSmallDesktop) {
    return renderSmallScreenView();
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
      component="section"
      aria-label="Budget details section"
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          mb: 3,
        }}
      >
        {title}
      </Typography>

      <TableContainer>
        <Table
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              borderBottom: 'none',
            },
          }}
          role="table"
          aria-label="Budget details table"
        >
          {renderTableHeader()}
          {renderTableBody()}
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 3,
          pt: 2,
        }}
        component="div"
        role="text"
        aria-label={`Total draw amount: ${drawTotal}`}
      >
        <Typography variant="h6">
          Draw Total: <FormattedCurrency value={drawTotal} />
        </Typography>
      </Box>
    </Paper>
  );
}
